import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import Countdown from 'react-countdown';
import { Trans, useTranslation } from 'react-i18next';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Link, useLocation, useParams } from 'react-router-dom';
import FeaturesSlider from '../../comp/FeaturesSlider';
import UserCardSlider from '../../comp/UserCardSlider';
import styles from './Home.module.scss';
import { formatTranslationNode } from '../../utils/utils';
import './styles.scss';

const Home = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [width, setWidth] = useState(window.innerWidth);
  const [smBreakpoint] = useState(767);
  const [is100pxFromTop, setIs100pxFromTop] = useState(false);
  const formBtnRef = useRef(null);
  const { lang } = useParams();

  // Scroll to a specific section
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({
      behavior: 'smooth',
      block: width > smBreakpoint ? 'center' : 'start',
      inline: 'nearest',
    });
  };

  // Check if the button is 100px from the top of the page
  useEffect(() => {
    const handleScroll = () => {
      if (formBtnRef.current) {
        const rect = formBtnRef.current.getBoundingClientRect();
        if (rect.top <= 100 && rect.top >= 0) {
          setIs100pxFromTop(true);
        } else {
          setIs100pxFromTop(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Countdown completion message
  const CompletionList = () => <p>IT'S ON!</p>;

  /**
   * Countdown renderer.
   * @param {Object} props - Props from Countdown component.
   */
  const renderer = ({
    completed,
    days,
    hours,
    minutes,
    formatted: {
      days: formattedDays,
      hours: formattedHours,
      minutes: formattedMinutes,
      seconds: formattedSeconds,
    },
  }) => {
    if (completed) {
      return <Fragment />;
    }
    return (
      <>
        <div className={styles.days}>
          <div className={styles.numbers}>
            <p>{formattedDays}</p>
          </div>
          <div className={styles.text}>DNI</div>
        </div>
        <div className={styles.hours}>
          <div className={styles.numbers}>
            <p>{formattedHours}</p>
          </div>
          <div className={styles.text}>GODZIN</div>
        </div>
        <div className={styles.minutes}>
          <div className={styles.numbers}>
            <p>{formattedMinutes}</p>
          </div>
          <div className={styles.text}>MINUT</div>
        </div>
        <div className={styles.minutes}>
          <div className={styles.numbers}>
            <p>{formattedSeconds}</p>
          </div>
          <div className={styles.text}>SEKUND</div>
        </div>
      </>
    );
  };

  const sponsors = [
    {
      image: require('../../../images/piercare.png'),
      link: 'https://pierccare.com/pl/',
    },
    {
      image: require('../../../images/stiletto.png'),
      link: 'https://www.stilettopiercinguk.com',
    },
    {
      image: require('../../../images/neilmed.png'),
      link: 'https://shopeu.neilmedpac.com/',
    },
    {
      image: require('../../../images/naga.png'),
      link: 'https://www.etsy.com/uk/shop/NAGABodyJewelry',
    },
    {
      image: require('../../../images/mick.png'),
      link: 'https://www.mickjewelry.pl',
    },
    {
      image: require('../../../images/oronegro.png'),
      link: 'https://www.instagram.com/oronegro.jewelry/',
    },
    {
      image: require('../../../images/piercingroom.png'),
      link: 'https://piercingroom.pl/',
    },
    {
      image: require('../../../images/poduszko.png'),
      link: 'https://poduszko.pl/',
    },
    {
      image: require('../../../images/pin.png'),
      link: 'https://www.picturesinblood.com/',
    },
    {
      image: require('../../../images/abs.png'),
      link: 'https://absolutebodyart.studio/',
    },
    {
      image: require('../../../images/susgear.png'),
      link: 'https://www.instagram.com/suspension_gear/',
    },
    {
      image: require('../../../images/bop.png'),
      link: 'https://www.instagram.com/BLISSOFPAIN/',
    },
    {
      image: require('../../../images/ABA.png'),
      link: 'http://altbody.art/',
    },
    {
      image: require('../../../images/dr.png'),
      link: 'https://piercingroom.pl/',
    },
  ];

  return (
    <>
      <Container fluid className={styles.homeWrapper}>
        <div id='home' className={styles.heroBG}>
          <Container className={styles.sectionWrap}>
            <Row className={styles.heroRow}>
              <Col className={styles.heroWrap}>
                <div className={styles.contentWrap}>
                  <div className={styles.headline}>
                    <h1>
                      <Trans i18nKey='hero-t-1' />
                      <br />{' '}
                      <span>
                        <Trans i18nKey='hero-t-2' />
                      </span>
                    </h1>
                    <p>
                      <Trans i18nKey='hero-p-1' />
                    </p>
                  </div>
                  <div className={styles.keyItems}>
                    <p>
                      <Trans i18nKey='featured-1' />
                    </p>
                    {/* <span>//</span>
                    <p>PLAY PIERCING</p> */}
                    <span>//</span>
                    <p>
                      <Trans i18nKey='featured-2' />
                    </p>
                  </div>
                  <div className={styles.location}>
                    <div className={styles.innerWrap}>
                      <span>
                        <FaRegCalendarAlt />
                      </span>
                      <p>
                        <Trans i18nKey='date' />
                      </p>
                    </div>
                    <div className={styles.innerWrap}>
                      <span>
                        <FaLocationDot />
                      </span>
                      <p>
                        <Trans i18nKey='location' />
                      </p>
                    </div>
                  </div>
                  <div className={styles.counterWrap}>
                    <Countdown
                      autoStart
                      date={'2026-05-27T14:00:00'}
                      intervalDelay={0}
                      precision={3}
                      renderer={renderer}
                    />
                  </div>
                </div>
                <div ref={formBtnRef} className={`${styles.buttonWrap}`}>
                  <Button
                    className={styles.btnPrimaryInverted}
                    as={Link}
                    to={`/${lang}/${t('register')}`}
                  >
                    <Trans i18nKey='register_label' />
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Container className={styles.sectionWrap}>
          <div className={styles.spacer}>
            <div className={styles.title}>
              <h2>
                <Trans i18nKey='head-1' />
              </h2>
            </div>
          </div>

          <Row className={styles.hostWrap}>
            <Col className={`${styles.wrap} host-carousel`}>
              <UserCardSlider />
            </Col>
          </Row>

          <div className={styles.spacer}>
            <div className={styles.title}>
              <h2>
                <Trans i18nKey='head-2' />
              </h2>
            </div>
          </div>

          <Row className={styles.aboutWrap}>
            <Col className={styles.wrap}>
              <div className={styles.imgWrap}>
                <LazyLoadImage
                  src={require('../../../images/about-photograph2.jpg')}
                  placeholderSrc={require('../../../images/transparent.png')}
                  alt={`https://www.instagram.com/modertribeevents/`}
                  className='img-lazy'
                />
              </div>
              <div className={styles.contentWrap}>
                <div className={styles.para}>
                  <p>
                    <Trans i18nKey='mt-desc' />
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* <div className={styles.spacer}>
            <div className={styles.title}>
              <h2>
                <Trans i18nKey="head-3" />
              </h2>
            </div>
          </div> */}

          {/* <Row className={styles.aboutWrap}>
            <Col className={`${styles.wrap} ${styles.reverse}`}>
              <div className={styles.imgWrap}>
                <LazyLoadImage
                  src={require("../../../images/ppp_banner.jpg")}
                  placeholderSrc={require("../../../images/transparent.png")}
                  alt={`https://www.instagram.com/modertribeevents/`}
                  className="img-lazy"
                />
              </div>
              <div className={styles.contentWrap}>
                <div className={styles.para}>
                  <p>
                    Podczas tegorocznej Konferencji Professional Piercers Poland ponownie zapraszamy Was do naszej przestrzeni – Modern Tribe. Przez cały weekend będziecie mogli podwiesić się w bezpiecznym, wspierającym otoczeniu, w gronie ludzi, którzy wiedzą, co robią i dlaczego to robią.
                  </p>
                  <p>
                    Nie zabraknie też strefy Piercers for Piercers – czyli tej niesamowitej energii, która pojawia się, gdy w jednym miejscu zbiera się tylu świetnych piercerów. Będzie z kim pogadać, będzie z kim działać – i będzie w czym wybierać.
                  </p>
                  <p>
                    Piątkowy wieczór? Jak zawsze – konkurs, luźna atmosfera, śmiech, rozmowy, nowe znajomości i totalny chill. To moment, żeby się spotkać, wymienić doświadczeniami i po prostu dobrze spędzić czas razem.
                  </p>
                  <p>
                    Co ważne – nie musisz brać udziału w całej konferencji, żeby odwiedzić naszą strefę. Jeśli chcesz się podwiesić, po prostu zarejestruj się wcześniej przez naszą stronę – i tyle.
                  </p>
                  <p>
                    Nie możemy się doczekać, aż znów razem stworzymy przestrzeń, w której liczy się autentyczność, szacunek i wspólnota. Do zobaczenia w Modern Tribe!
                  </p>
                </div>
              </div>
            </Col>
          </Row> */}

          {/* <div className={styles.spacerMd} /> */}

          <Row className={styles.featuresWrap}>
            <Col
              className={`${styles.wrap} host-carousel feature adjust-arrows`}
            >
              <FeaturesSlider />
              <div ref={formBtnRef} className={`${styles.buttonWrap}`}>
                <Button
                  className={styles.btnPrimaryInverted}
                  as={Link}
                  to={`/${lang}/${t('register')}`}
                  state={{ previousLocation: location }}
                >
                  <Trans i18nKey={'register_label'} />
                </Button>
              </div>
            </Col>
          </Row>

          <Row className={styles.sponsorsWrap}>
            <div className={styles.spacer}>
              <div className={styles.title}>
                <h2>
                  <Trans i18nKey='head-4' />
                </h2>
              </div>
            </div>
            <div id='parallax'>
              <Col className={`${styles.wrap}`}>
                <div className={styles.innerWrap}>
                  {sponsors.map(({ image, link }, i) => (
                    <div key={i} className={styles.imageWrap}>
                      <div
                        className={`${styles.inner} ${i < 3 || i >= sponsors.length - 2 ? styles.primary : ''}`}
                      >
                        <img
                          src={image}
                          onClick={() => {
                            window.open(link, '_blank', 'noopener,noreferrer');
                          }}
                          alt='SPONSOR LOGO'
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </div>
          </Row>

          <Row id='contact' className={styles.contactWrap}>
            <div className={styles.spacer}>
              <div className={styles.title}>
                <h2>
                  <Trans i18nKey='head-5' />
                </h2>
              </div>
            </div>
            <Col className={styles.wrap}>
              <div className={styles.contact}>
                <div className={styles.inner}>
                  <p>
                    {formatTranslationNode('faq-desc', [
                      <Link
                        key='faq'
                        to={`/${lang}/terms/faqs`}
                        state={{ previousLocation: location }}
                      >
                        FAQ
                      </Link>,
                    ])}
                  </p>
                  <Button
                    as={Link}
                    to={`/${lang}/contact`}
                    state={{ previousLocation: location }}
                    className={styles.btnPrimaryInverted}
                  >
                    <Trans i18nKey={'contact'} />
                  </Button>
                </div>
              </div>
              <div className={styles.map}>
                <iframe
                  src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2603.001330904568!2d19.882721!3d49.276371999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x471592cbec323313%3A0x9a50cd76a41cb79d!2sChata%20MTB%20Ko%C5%9Bcielisko!5e0!3m2!1spl!2suk!4v1761442889520!5m2!1spl!2suk'
                  width='600'
                  height='450'
                  style={{
                    border: 0,
                    width: '100%',
                    height: '100%',
                    minHeight: '500px',
                  }}
                  allowFullScreen
                  loading='lazy'
                  referrerPolicy='no-referrer-when-downgrade'
                />
              </div>
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};

export default Home;
